import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { useCrossFlowFrontend } from "@/components/cross-flow/use-cross-flow-frontend";
import { ContentBlock } from "@/components/wpl/content-block/src";
import { Link } from "@/components/wpl/link/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

const CTASection = () => {
    const intl = useIntl();
    const signupLink = useLocalizedHref("/software/jira/free");
    const { openCrossFlow } = useCrossFlowFrontend(signupLink);

    const ctaButtonText = intl.formatMessage({
        id: "JiraSoftwarePageTemplate.ctaSection.getItFree",
        defaultMessage: "Get it free"
    });

    const linkDataEventProps: DataEventProps = {
        "data-event-component": "linkButton",
        "data-event-container": "ctaSection",
        "data-event": "clicked",
        "data-product-key": "jiraSoftware",
        "data-label": ctaButtonText,
        "data-label-english": "Get it free",
        "data-track-event": "jiraSoftware.get-it-free-button-clicked"
    };

    return (
        <section
            css={css({
                "alignItems": "center",
                "backgroundColor": "#091e42",
                "color": "#ffffff",
                "display": "flex",
                "flexDirection": "column",
                "gap": "24px",
                "padding": "32px 0",
                "h2": {
                    fontSize: "2.25rem",
                    fontFamily: "var(--bxp-charlie-display-font-family)",
                    fontWeight: "500"
                },
                [media.below.sm]: {
                    h2: {
                        fontSize: "1.5rem",
                        fontFamily: "var(--bxp-charlie-display-font-family)",
                        fontWeight: "500"
                    }
                },
                ".features-link": {
                    "svg": {
                        transition: "transform 0.25s ease-in-out",
                        path: {
                            fill: "#fafbfc"
                        }
                    },

                    "&:focus": {
                        color: "#fafbfc"
                    },
                    "&:visited": {
                        color: "#fafbfc"
                    },
                    "&:hover": {
                        color: "#fafbfc",
                        textDecoration: "underline",
                        svg: {
                            transform: `translateX(${token("space.100")})`
                        }
                    }
                }
            })}
        >
            <ContentBlock
                headerType="h2"
                headerText={intl.formatMessage({
                    id: "JiraSoftwarePageTemplate.ctaSection.getStarted",
                    defaultMessage: "Get Started with Jira"
                })}
                orientation="vertical"
                ctaProps={{
                    buttonProps: {
                        text: intl.formatMessage({
                            id: "JiraSoftwarePageTemplate.ctaSection.getItFree",
                            defaultMessage: "Get it free"
                        }),
                        color: "yellow",
                        url: signupLink,
                        dataEventProps: linkDataEventProps,
                        onClick: openCrossFlow
                    }
                }}
                isCentered
            />
            <Link
                className="features-link"
                href={useLocalizedHref("/software/jira/features")}
                text={intl.formatMessage({
                    id: "JiraSoftwarePageTemplate.ctaSection.seeFeatures",
                    defaultMessage: "See features"
                })}
                bgColor="bgDark"
                dataEventProps={linkDataEventProps}
            />
        </section>
    );
};

CTASection.displayName = "CTASection";

export default CTASection;
